import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Nav, NavItem, NavLink, Button, TabContent, TabPane } from 'reactstrap';
import classnames from 'classnames';
import 'bootstrap/dist/css/bootstrap.css';
import "react-table/react-table.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from '@fortawesome/free-solid-svg-icons'
import { faStar as faStarOutline } from '@fortawesome/free-regular-svg-icons'
import AddContact2 from './AddContact2';
import ContactsLanding2 from './ContactsLanding2';
import ContactsNotesForm2 from './ContactsNotesForm2';
import ContactsDemographics2 from './ContactsDemographics2';
import CompanyContext from '../../auth/CompanyContext';
import ConfirmButton from '../Modal/ConfirmButton';
import ContactsAccounts2 from './ContactsAccounts2';
import PermissionWrapper from '../../auth/PermissionWrapper';
import ContactsDocuments2 from './ContactsDocuments2';
import ContactsNDA2 from './ContactsNDA2';
import ContactsMarketingCampaigns2 from './ContactsMarketingCampaigns2';
import ContactsInvestments2 from './ContactsInvestments2';
import ContactsCore2 from './ContactsCore2';
import ContactsDashboard2 from './ContactsDashboard2';
import * as utilities from '../../utilities';
import CRMContext from '../../auth/CRMContext';

const DETAILS_FIELDS = ['Contact Name', 'Title', 'Phone Number', 'Email Address', 'Inactive', 'Note', 'LinkedIn URL'];

const ContactsDetail2 = ({
    clientToken,
    contacts,
    editID,
    setEditID,
    selectedRow,
    setSelectedRow,
    companies,
    setCompanies,
    refreshContacts,
    toggleModal,
    editCategoryCommentsArray,
    addCategoryCommentsArray,
    addCategorySubCommentsArray,
    notesCategories,
    setEditCategoryCommentsArray,
    setAddCategoryCommentsArray,
    handleEditSwap,
    setModalOpen,
    user,
    addFavorite,
    removeFavorite,
    favoriteCallback,
    companySettings,
    idToken,
    userPermissions,
    onContactDeleted,
    onContactPatched,
    onUnsavedChangesChange
}) => {
    const companyCtx = useContext(CompanyContext);
    const crmCtx = useContext(CRMContext);
    const crmEnabled = crmCtx?.crmEnabled === true;
    const hasCompanyPortalDatabase = String(companySettings?.CompanyPortalDatabase || '').trim() !== '';
    const mountedRef = useRef(true);

    const [loading, setLoading] = useState(false);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [activeTab, setActiveTab] = useState(parseInt(localStorage.getItem('contactsActiveTab') || 10));
    const [notesCount, setNotesCount] = useState(0);
    const [documentsCount, setDocumentsCount] = useState(0);
    const [marketingCampaignsCount, setMarketingCampaignsCount] = useState(0);
    const [contactAccountsVersion, setContactAccountsVersion] = useState(0);
    const [contactAccountsLoading, setContactAccountsLoading] = useState(!!editID);
    const [selectedContactHouseholds, setSelectedContactHouseholds] = useState([]);
    const [householdRows, setHouseholdRows] = useState([]);
    const [householdsLoading, setHouseholdsLoading] = useState(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        if (typeof onUnsavedChangesChange === 'function') {
            onUnsavedChangesChange(unsavedChanges);
        }
    }, [unsavedChanges, onUnsavedChangesChange]);

    const displayEditCategories = useCallback((val) => {
        const normalizedVal = String(val || '').toLowerCase();
        const contactFromList = contacts?.find(item => String(item.linkedID || '').toLowerCase() === normalizedVal) || null;
        const fallbackSelectedRow = (
            (!contacts || contacts.length === 0)
            && String(selectedRow?.linkedID || '').toLowerCase() === normalizedVal
        ) ? selectedRow : null;
        const dataList = contactFromList || fallbackSelectedRow;

        if (!dataList || !dataList.children) {
            return;
        }

        // clone categories so we don’t mutate notesCategories
        const categoryList = (notesCategories || []).map(item => ({ ...item }));

        categoryList.forEach(item => {
            const fieldKey = item.fieldKey || item.type;
            if (Object.prototype.hasOwnProperty.call(dataList.children, fieldKey)) {
                item.value = dataList.children[fieldKey];
            } else if (Object.prototype.hasOwnProperty.call(dataList.children, item.type)) {
                item.value = dataList.children[item.type];
            } else {
                item.value = "";
            }
        });

        const inactiveIdx = categoryList.findIndex(item => item.type === "Inactive");
        if (inactiveIdx > -1) {
            categoryList[inactiveIdx].value = !dataList.active;
        }

        setEditCategoryCommentsArray(categoryList);
    }, [contacts, notesCategories, selectedRow]);

    const hydrateFromPropsIfReady = useCallback(() => {
        if (unsavedChanges) return;

        const nextEditID = editID;
        const nextSelectedRow = selectedRow;

        if (nextEditID) {
            if (nextSelectedRow && notesCategories.length > 0) {
                setSelectedRow(nextSelectedRow);
                displayEditCategories(nextEditID);
            }
            return;
        }

        if (!nextEditID) {
            setSelectedRow(null);
            setEditCategoryCommentsArray([]);
        }
    }, [
        editID,
        selectedRow,
        notesCategories.length,
        displayEditCategories,
        unsavedChanges
    ]);

    useEffect(() => {
        hydrateFromPropsIfReady();
    }, [hydrateFromPropsIfReady]);

    const changeComment = useCallback((e, validation = null) => {
        setEditCategoryCommentsArray(prev => {
            const dataArray = [...prev];
            const field = dataArray.find(item => parseInt(item.ID) === parseInt(e.target.name));
            if (!field) return prev;

            if (e.target.type === 'select-multiple') {
                field.value = Array.from(e.target.selectedOptions, option => option.value);
            } else if (e.target.type === 'checkbox') {
                field.value = e.target.checked;
            } else {
                field.value = e.target.value;
            }

            if (validation != null) {
                const matches = String(field.value).match(new RegExp(validation));
                field.error = matches == null;
            }

            if (field.value === '' || field.value == null) {
                field.error = false;
            }

            return dataArray;
        });

        setUnsavedChanges(true);
    }, []);

    const setComment = useCallback((e, validation = null) => {
        setAddCategoryCommentsArray(prev => {
            const dataArray = [...prev];
            const field = dataArray.find(item => parseInt(item.ID) === parseInt(e.target.name));
            if (!field) return prev;

            if (e.target.type === 'select-multiple') {
                field.value = Array.from(e.target.selectedOptions, option => option.value);
            } else if (e.target.type === 'checkbox') {
                field.value = e.target.checked;
            } else {
                field.value = e.target.value;
            }

            if (validation != null) {
                const matches = String(field.value).match(new RegExp(validation));
                field.error = (matches == null);
            }

            if (field.value === '' || field.value == null) {
                field.error = false;
            }

            return dataArray;
        });

        setUnsavedChanges(true);
    }, []);

    const isFormBlank = useCallback((array) => {
        let retBool = true;
        (array || []).forEach(row => {
            if (row.value !== null && row.value !== '') retBool = false;
        });
        return retBool;
    }, []);

    const handleAddSubmit = useCallback(() => {
        let dataObj = {};
        let active = true;

        (addCategoryCommentsArray || [])
            .filter(item => item.value !== '' && item.value != null)
            .forEach(item => {
                if (item.type === 'Inactive' && (item.value === 1 || Boolean(item.value))) {
                    active = false;
                } else {
                    const fieldKey = item.fieldKey || item.type;
                    dataObj[fieldKey] = item.value;
                    if (fieldKey !== item.type) dataObj[item.type] = item.value;
                }
            });

        setLoading(true);

        fetch('/api/contacts/companies/', {
            method: 'POST',
            body: JSON.stringify({
                data: JSON.stringify(dataObj),
                companies,
                auth0: user,
                active
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(r => r.json())
            .then(results => {
                if (!mountedRef.current) return;

                if (results.debug_message == null) {
                    setAddCategoryCommentsArray([]);
                    setLoading(false);
                    setUnsavedChanges(false);

                    refreshContacts && refreshContacts();
                    toggleModal && toggleModal();
                } else {
                    setLoading(false);
                }
            })
            .catch(() => {
                if (!mountedRef.current) return;
                setLoading(false);
            });
    }, [addCategoryCommentsArray, companies, user, clientToken, refreshContacts, toggleModal, companyCtx.company]);

    const updateContactCompanies = useCallback((contactGUID, nextCompanySecIDs) => {
        return fetch('/api/contacts/companies/' + contactGUID, {
            method: 'PUT',
            body: JSON.stringify({
                companies: Array.isArray(nextCompanySecIDs) ? nextCompanySecIDs : []
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(r => r.json())
            .then(results => {
                if (results?.debug_message != null || results?.success === false) {
                    throw new Error('Unable to update contact companies');
                }
                return results;
            });
    }, [clientToken, companyCtx.company]);

    const handleEditSubmit = useCallback((e) => {
        let dataObj = {};
        let active = true;
        const contactGUID = e.target.value;

        (editCategoryCommentsArray || [])
            .filter(item => item.value !== '' && item.value != null)
            .forEach(item => {
                if (item.type === 'Inactive' && (item.value === 1 || Boolean(item.value))) {
                    active = false;
                } else {
                    const fieldKey = item.fieldKey || item.type;
                    dataObj[fieldKey] = item.value;
                    if (fieldKey !== item.type) dataObj[item.type] = item.value;
                }
            });

        setLoading(true);

        fetch('/api/contacts/' + contactGUID, {
            method: 'PUT',
            body: JSON.stringify({
                data: JSON.stringify(dataObj),
                auth0: user,
                active
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(r => r.json())
            .then(results => {
                if (results.debug_message != null) throw new Error('Edit failed');

                return updateContactCompanies(
                    contactGUID,
                    companies != null ? companies.map(c => c.secID) : []
                );
            })
            .then(() => {
                if (!mountedRef.current) return;

                const nextChildren = { ...dataObj };
                const nextCompanies = Array.isArray(companies) ? companies : [];
                if (typeof onContactPatched === 'function') {
                    onContactPatched({
                        linkedID: contactGUID,
                        children: nextChildren,
                        active: !!active,
                        companies: nextCompanies
                    });
                }

                setUnsavedChanges(false);
                setLoading(false);
                utilities.addToast('Contact saved successfully.', 3, 'success');
            })
            .catch(() => {
                if (!mountedRef.current) return;
                setLoading(false);
            });
    }, [
        editCategoryCommentsArray,
        companies,
        user,
        clientToken,
        companyCtx.company,
        updateContactCompanies,
        onContactPatched
    ]);

    const handleDelete = useCallback((e) => {
        const deletedGUID = String(e?.target?.value || editID || '').trim();
        fetch('/api/contacts/' + deletedGUID, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(r => r.json())
            .then(results => {
                if (!mountedRef.current) return;

                if (results.debug_message == null) {
                    utilities.addToast('Contact removed successfully.', 3, 'success');

                    setLoading(false);
                    setUnsavedChanges(false);
                    if (typeof onContactDeleted === 'function') onContactDeleted(deletedGUID);

                    if (typeof handleEditSwap === 'function') {
                        handleEditSwap('');
                    } else {
                        setEditID(0);
                        toggleModal && toggleModal();
                    }
                }
            })
            .catch(() => { /* ignore */ });
    }, [clientToken, companyCtx.company, toggleModal, handleEditSwap, onContactDeleted, editID]);

    const displayCategories = useCallback((e) => {
        // preserved for parity (even though selectValue isn't used elsewhere)
        const categoryList =
            notesCategories.filter(item => parseInt(item.ID) === parseInt(e.target.value))?.[0]?.children || [];
        categoryList.forEach((item) => { item.value = null; });
        // if you later re-add selectValue state, set it here
    }, [notesCategories]);

    const onSelectAutocomplete = useCallback((val) => {
        const secID = String(val?.sec || '').trim();
        if (!secID) return;

        const current = Array.isArray(companies) ? companies : [];
        if (current.some(c => String(c.secID) === secID)) return;

        const next = [...current, { primaryBusinessName: val.name, secID }];
        if (setCompanies) setCompanies(next);
        if (!editID) return;

        updateContactCompanies(editID, next.map(c => c.secID))
            .then(() => {
                if (!mountedRef.current) return;
                if (typeof onContactPatched === 'function') {
                    onContactPatched({
                        linkedID: editID,
                        companies: next
                    });
                }
            })
            .catch(() => {
                if (!mountedRef.current) return;
                if (setCompanies) setCompanies(current);
                utilities.addToast('Unable to add company. Please try again.', 5, 'danger');
            });
    }, [companies, setCompanies, editID, updateContactCompanies, onContactPatched]);

    const removeCompany = useCallback((secID) => {
        const current = Array.isArray(companies) ? companies : [];
        const next = current.filter(c => c.secID !== secID);
        if (next.length === current.length) return;

        if (setCompanies) setCompanies(next);
        if (!editID) return;

        updateContactCompanies(editID, next.map(c => c.secID))
            .then(() => {
                if (!mountedRef.current) return;
                if (typeof onContactPatched === 'function') {
                    onContactPatched({
                        linkedID: editID,
                        companies: next
                    });
                }
            })
            .catch(() => {
                if (!mountedRef.current) return;
                if (setCompanies) setCompanies(current);
                utilities.addToast('Unable to remove company. Please try again.', 5, 'danger');
            });
    }, [companies, setCompanies, editID, updateContactCompanies, onContactPatched]);

    const closeModal = useCallback(() => {
        setEditCategoryCommentsArray([]);
        setEditID(0);
        setUnsavedChanges(false);
    }, []);

    const handleCancel = useCallback(() => {
        setAddCategoryCommentsArray([]);
        setModalOpen(false);
        setUnsavedChanges(false);
    }, []);

    const onContactAccountsChanged = useCallback((accounts = []) => {
        if (Array.isArray(accounts)) {
            setSelectedContactHouseholds(accounts);
        }
        setContactAccountsVersion(prev => prev + 1);
    }, []);

    const getContactAccounts = useCallback((nextContactGUID = editID) => {
        if (!nextContactGUID) {
            setSelectedContactHouseholds([]);
            return;
        }

        setContactAccountsLoading(true);
        fetch(`/api/contactAccounts/${nextContactGUID}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + (clientToken || ""),
                'X-Database': companyCtx.company
            }
        })
            .then(results => results.json())
            .then(results => {
                if (!mountedRef.current) return;
                if (results.success) {
                    const accounts = (results.data || []).map(item => item.account).filter(Boolean);
                    setSelectedContactHouseholds(accounts);
                } else {
                    utilities.addToast('Unable to load contact accounts.', 5, 'danger');
                }
            })
            .catch(() => {
                utilities.addToast('An error has occurred.', 5, 'danger');
            })
            .finally(() => {
                if (!mountedRef.current) return;
                setContactAccountsLoading(false);
            });
    }, [editID, clientToken, companyCtx.company]);

    const getHouseholdInfo = useCallback(() => {
        if (!idToken || !companyCtx.company || !hasCompanyPortalDatabase) {
            setHouseholdRows([]);
            return;
        }

        setHouseholdsLoading(true);
        fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/householdinfo`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + idToken,
                'X-Database': companyCtx.company
            }
        })
            .then(results => results.json())
            .then(results => {
                if (!mountedRef.current) return;
                setHouseholdRows(results?.data || []);
            })
            .catch(() => {
                utilities.addToast('Unable to load households.', 5, 'danger');
                if (!mountedRef.current) return;
                setHouseholdRows([]);
            })
            .finally(() => {
                if (!mountedRef.current) return;
                setHouseholdsLoading(false);
            });
    }, [idToken, companyCtx.company, hasCompanyPortalDatabase]);

    useEffect(() => {
        if (!crmEnabled || !hasCompanyPortalDatabase) {
            setHouseholdRows([]);
            setSelectedContactHouseholds([]);
            return;
        }

        if (!editID) {
            setHouseholdRows([]);
            setSelectedContactHouseholds([]);
            return;
        }

        if (householdsLoading || householdRows.length > 0) return;

        getHouseholdInfo();
    }, [crmEnabled, hasCompanyPortalDatabase, editID, householdsLoading, householdRows.length, getHouseholdInfo]);

    useEffect(() => {
        if (!crmEnabled || !hasCompanyPortalDatabase) {
            setSelectedContactHouseholds([]);
            setContactAccountsLoading(false);
            return;
        }

        if (!editID) {
            setSelectedContactHouseholds([]);
            setContactAccountsLoading(false);
            return;
        }
        getContactAccounts(editID);
    }, [crmEnabled, hasCompanyPortalDatabase, editID, getContactAccounts]);

    useEffect(() => { setNotesCount(0); }, [editID]);
    useEffect(() => { setDocumentsCount(0); }, [editID, crmEnabled]);
    useEffect(() => { setMarketingCampaignsCount(0); }, [editID]);

    useEffect(() => {
        if (crmEnabled && hasCompanyPortalDatabase) return;
        if ([3, 5, 7, 8].includes(activeTab)) {
            setActiveTab(1);
            localStorage.setItem('contactsActiveTab', 1);
        }
    }, [crmEnabled, hasCompanyPortalDatabase, activeTab]);

    const isEditing = !!editID;              // editID is UUID or 0
    const showEditor = isEditing;            // if editing, always show editor UI

    // When editing, avoid "No Contact Name" by using selectedRow as fallback:
    const contactName =
        editCategoryCommentsArray?.find(i => i.type === 'Contact Name')?.value ??
        selectedRow?.children?.['Contact Name'] ??
        '';

    const title =
        editCategoryCommentsArray?.find(i => i.type === 'Title')?.value ??
        selectedRow?.children?.['Title'] ??
        '';

    const isDetailsField = useCallback((item) => {
        const screenTab = String(item?.screenTab || '').trim().toLowerCase();
        if (screenTab === 'details') return true;
        if (screenTab === 'demographics') return false;
        return DETAILS_FIELDS.some(field =>
            item.type?.toLowerCase().includes(field.toLowerCase())
        );
    }, []);

    const isFavorite = !!contacts?.find(c => c.linkedID === editID)?.favorites;
    const ndaEmail = (
        editCategoryCommentsArray?.find(item => item.type === 'Email Address')?.value
        || editCategoryCommentsArray?.find(item => item.type === 'Email Address (Office)')?.value
        || editCategoryCommentsArray?.find(item => item.type === 'Email Address (Personal)')?.value
        || selectedRow?.children?.['Email Address']
        || selectedRow?.children?.['Email Address (Office)']
        || selectedRow?.children?.['Email Address (Personal)']
        || ''
    );
    const ndaPhone = (
        editCategoryCommentsArray?.find(item => item.type === 'Phone Number')?.value
        || editCategoryCommentsArray?.find(item => item.type === 'Phone Number (Mobile)')?.value
        || editCategoryCommentsArray?.find(item => item.type === 'Phone Number (Office)')?.value
        || selectedRow?.children?.['Phone Number']
        || selectedRow?.children?.['Phone Number (Mobile)']
        || selectedRow?.children?.['Phone Number (Office)']
        || ''
    );
    const ndaCompanyName = (
        editCategoryCommentsArray?.find(item => item.type === 'Company Name')?.value
        || selectedRow?.children?.['Company Name']
        || ''
    );
    const ndaContactSnapshot = {
        contactName: String(contactName || ''),
        title: String(title || ''),
        email: String(ndaEmail || ''),
        phone: String(ndaPhone || ''),
        companyName: String(ndaCompanyName || '')
    };

    return (
        <Container fluid className='px-0'>
            {showEditor ? (
                <>
                    <Row className='align-items-start'>
                        <Col md={4} className='text-left companyDetails pl-5 py-3'>
                            {['Phone Number', 'Phone Number (Office)', 'Phone Number (Mobile)'].map((attr, idx) => (
                                <div key={idx}>
                                    {editCategoryCommentsArray?.find(item => item.type === attr)?.value ? <>{editCategoryCommentsArray?.find(item => item.type === attr)?.value} - {attr}<br /></> : <></>}
                                </div>
                            ))}
                        </Col>
                        <Col md={4} className='text-center'>
                            <h1>{contactName || ''}</h1>
                            {title ? <h6>{title}</h6> : null}
                        </Col>
                        <Col md={4} className='text-right companyDetails pr-5'>
                            <Row>
                                <Col xs="12" className="text-right" >
                                    {isFavorite ?
                                        <FontAwesomeIcon icon={faStar} className="favoriteIcon" onClick={() => removeFavorite('contact', editID, favoriteCallback)} />
                                        : <FontAwesomeIcon icon={faStarOutline} className="favoriteIconDisabled" onClick={() => addFavorite('contact', editID, favoriteCallback)} />
                                    }
                                    {unsavedChanges ? (
                                        <ConfirmButton
                                            className="ml-3 mb-2"
                                            color="primary"
                                            onClick={() => handleEditSwap('')}
                                            title="Back to Contacts List"
                                            body="Are you sure you wish to discard your edits?"
                                        >
                                            Back to Contacts List
                                        </ConfirmButton>
                                    ) : (
                                        <Button
                                            className="ml-3 mb-2"
                                            color="primary"
                                            onClick={() => handleEditSwap('')}
                                            title="Back to Contacts List"
                                        >
                                            Back to Contacts List
                                        </Button>

                                    )}
                                </Col>
                            </Row>
                            {['Email Address', 'Email Address (Office)', 'Email Address (Personal)'].map((attr, idx) => (
                                <div key={idx}>
                                    {editCategoryCommentsArray?.find(item => item.type === attr)?.value ? <><a href={`mailto:${editCategoryCommentsArray?.find(item => item.type === attr)?.value}`}>{editCategoryCommentsArray?.find(item => item.type === attr)?.value}</a> - {attr}<br /></> : <></>}
                                </div>
                            ))}
                            {/* <div className='mt-3 text-left border rounded p-2 bg-light'>
                                
                            </div> */}
                        </Col>
                    </Row>
                    <Row className='pt-4 gradientNav'>
                        <Col>
                            <Nav tabs className="pb-0 px-2">
                                <NavItem >
                                    <NavLink
                                        className={classnames({ active: activeTab === 10 })}
                                        onClick={() => { setActiveTab(10); localStorage.setItem('contactsActiveTab', 10); }}>
                                        Core
                                    </NavLink>
                                </NavItem>
                                <NavItem >
                                    <NavLink
                                        className={classnames({ active: activeTab === 1 })}
                                        onClick={() => { setActiveTab(1); localStorage.setItem('contactsActiveTab', 1); }}>
                                        Details
                                    </NavLink>
                                </NavItem>
                                <NavItem >
                                    <NavLink
                                        className={classnames({ active: activeTab === 4 })}
                                        onClick={() => { setActiveTab(4); localStorage.setItem('contactsActiveTab', 4); }}>
                                        Demographics
                                    </NavLink>
                                </NavItem>
                                <NavItem >
                                    <NavLink
                                        className={classnames({ active: activeTab === 2 })}
                                        onClick={() => { setActiveTab(2); localStorage.setItem('contactsActiveTab', 2); }}>
                                        Notes {notesCount > 0 ? `(${notesCount})` : ''}
                                    </NavLink>
                                </NavItem>
                                {crmEnabled && (
                                    <PermissionWrapper userPermissions={userPermissions} allowedPermission="CRM Integration" hidden={true}>
                                        <NavItem >
                                            <NavLink
                                                className={classnames({ active: activeTab === 3 })}
                                                onClick={() => { setActiveTab(3); localStorage.setItem('contactsActiveTab', 3); }}>
                                                Dashboard
                                            </NavLink>
                                        </NavItem>
                                    </PermissionWrapper>
                                )}
                                {crmEnabled && (
                                    <PermissionWrapper userPermissions={userPermissions} allowedPermission="CRM Integration" hidden={true}>
                                        <NavItem >
                                            <NavLink
                                                className={classnames({ active: activeTab === 7 })}
                                                onClick={() => { setActiveTab(7); localStorage.setItem('contactsActiveTab', 7); }}>
                                                Documents {documentsCount > 0 ? `(${documentsCount})` : ''}
                                            </NavLink>
                                        </NavItem>
                                    </PermissionWrapper>
                                )}
                                <PermissionWrapper userPermissions={userPermissions} allowedPermission="PDC Flow" hidden={true}>
                                    <NavItem >
                                        <NavLink
                                            className={classnames({ active: activeTab === 9 })}
                                            onClick={() => { setActiveTab(9); localStorage.setItem('contactsActiveTab', 9); }}>
                                            NDA Forms
                                        </NavLink>
                                    </NavItem>
                                </PermissionWrapper>
                                {crmEnabled && (
                                    <PermissionWrapper userPermissions={userPermissions} allowedPermission="CRM Integration" hidden={true}>
                                        <NavItem >
                                            <NavLink
                                                className={classnames({ active: activeTab === 5 })}
                                                onClick={() => { setActiveTab(5); localStorage.setItem('contactsActiveTab', 5); }}>
                                                Investments
                                            </NavLink>
                                        </NavItem>
                                    </PermissionWrapper>
                                )}
                                <NavItem >
                                    <NavLink
                                        className={classnames({ active: activeTab === 6 })}
                                        onClick={() => { setActiveTab(6); localStorage.setItem('contactsActiveTab', 6); }}>
                                        Marketing/Campaigns {marketingCampaignsCount > 0 ? `(${marketingCampaignsCount})` : ''}
                                    </NavLink>
                                </NavItem>
                                {crmEnabled && (
                                    <PermissionWrapper userPermissions={userPermissions} allowedPermission="CRM Integration" hidden={true}>
                                        <PermissionWrapper userPermissions={userPermissions} allowedPermission="Contact Accounts" hidden={true}>
                                            <NavItem >
                                                <NavLink
                                                    className={classnames({ active: activeTab === 8 })}
                                                    onClick={() => { setActiveTab(8); localStorage.setItem('contactsActiveTab', 8); }}>
                                                    Account Mappings
                                                </NavLink>
                                            </NavItem>
                                        </PermissionWrapper>
                                    </PermissionWrapper>
                                )}
                            </Nav>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId={10} className="p-2 mt-2">
                                    <ContactsCore2
                                        clientToken={clientToken}
                                        idToken={idToken}
                                        contactGUID={editID}
                                        isActive={activeTab === 10}
                                        row={selectedRow}
                                        editCategoryCommentsArray={editCategoryCommentsArray}
                                        companies={companies}
                                        selectedContactHouseholds={selectedContactHouseholds}
                                        contactAccountsLoading={contactAccountsLoading}
                                        householdRows={householdRows}
                                        householdsLoading={householdsLoading}
                                        companySettings={companySettings}
                                        crmEnabled={crmEnabled}
                                        onNotesCountChange={setNotesCount}
                                    />
                                </TabPane>
                            </TabContent>

                            <TabContent activeTab={activeTab}>
                                <TabPane tabId={1} className="p-2 mt-2">
                                    <ContactsLanding2
                                        row={selectedRow}
                                        editCategoryCommentsArray={
                                            [...editCategoryCommentsArray].filter(
                                                item => isDetailsField(item)
                                            )
                                        }
                                        changeComment={changeComment}
                                        handleEditSubmit={handleEditSubmit}
                                        handleDelete={handleDelete}
                                        loading={loading}
                                        isFormBlank={isFormBlank}
                                        unsavedChanges={unsavedChanges}
                                        editID={editID}
                                        clientToken={clientToken}
                                        companies={companies}
                                        removeCompany={removeCompany}
                                        onSelectAutocomplete={onSelectAutocomplete}
                                    />
                                </TabPane>
                            </TabContent>

                            <TabContent activeTab={activeTab}>
                                <TabPane tabId={2} className="p-2 mt-2">
                                    <ContactsNotesForm2
                                        clientToken={clientToken}
                                        row={selectedRow}
                                        editID={editID}
                                        user={user}
                                        handleEditSwap={handleEditSwap}
                                        displayCategories={displayCategories}
                                        addCategorySubCommentsArray={addCategorySubCommentsArray}
                                        setNotesCount={setNotesCount}
                                    />
                                </TabPane>
                            </TabContent>

                            <TabContent activeTab={activeTab}>
                                <TabPane tabId={3} className="p-2 mt-2">
                                    <ContactsDashboard2
                                        clientToken={clientToken}
                                        idToken={idToken}
                                        contactGUID={editID}
                                        contactAccountsVersion={contactAccountsVersion}
                                        contactAccountsLoading={contactAccountsLoading}
                                        selectedContactHouseholds={selectedContactHouseholds}
                                        householdRows={householdRows}
                                        householdsLoading={householdsLoading}
                                        companySettings={companySettings}
                                        crmEnabled={crmEnabled}
                                        row={selectedRow}
                                        editCategoryCommentsArray={editCategoryCommentsArray}
                                        companies={companies}
                                        user={user}
                                        userPermissions={userPermissions}
                                        company={companyCtx.company}
                                        refreshUser={() => { }}
                                        isActive={activeTab === 3}
                                    />
                                </TabPane>
                            </TabContent>

                            <TabContent activeTab={activeTab}>
                                <TabPane tabId={4} className="p-2 mt-2">

                                    <ContactsDemographics2
                                        row={selectedRow}
                                        editCategoryCommentsArray={
                                            [...editCategoryCommentsArray].filter(
                                                item => !isDetailsField(item)
                                            )
                                        }
                                        changeComment={changeComment}
                                        handleEditSubmit={handleEditSubmit}
                                        handleDelete={handleDelete}
                                        loading={loading}
                                        isFormBlank={isFormBlank}
                                        unsavedChanges={unsavedChanges}
                                        editID={editID}
                                    />


                                </TabPane>
                            </TabContent>

                            <TabContent activeTab={activeTab}>
                                <TabPane tabId={5} className="p-2 mt-2">
                                    {crmEnabled && (
                                        <ContactsInvestments2
                                            idToken={idToken}
                                            isActive={activeTab === 5}
                                            selectedContactHouseholds={selectedContactHouseholds}
                                            contactAccountsLoading={contactAccountsLoading}
                                            householdRows={householdRows}
                                            householdsLoading={householdsLoading}
                                            companySettings={companySettings}
                                        />
                                    )}
                                </TabPane>
                            </TabContent>

                            <TabContent activeTab={activeTab}>
                                <TabPane tabId={6} className="p-2 mt-2">
                                    <ContactsMarketingCampaigns2
                                        clientToken={clientToken}
                                        contactGUID={editID}
                                        contact={selectedRow}
                                        companies={companies}
                                        salesActivityFields={addCategorySubCommentsArray}
                                        isActive={activeTab === 6}
                                        onCountChange={setMarketingCampaignsCount}
                                        user={user}
                                    />
                                </TabPane>
                            </TabContent>

                            <TabContent activeTab={activeTab}>
                                <TabPane tabId={7} className="p-2 mt-2">
                                    {crmEnabled && (
                                        <ContactsDocuments2
                                            {...{
                                                ...{
                                                    clientToken,
                                                    idToken,
                                                    contactGUID: editID,
                                                    contactAccountsVersion,
                                                    selectedContactHouseholds,
                                                    householdRows,
                                                    householdsLoading,
                                                    onRefreshHouseholds: getHouseholdInfo,
                                                    onDocumentsCountChange: setDocumentsCount,
                                                    companySettings,
                                                    user,
                                                    company: companyCtx.company,
                                                }
                                            }}
                                        />
                                    )}
                                </TabPane>
                            </TabContent>

                            {crmEnabled && (
                                <PermissionWrapper userPermissions={userPermissions} allowedPermission="CRM Integration" hidden={true}>
                                    <PermissionWrapper userPermissions={userPermissions} allowedPermission="Contact Accounts" hidden={true}>
                                        <TabContent activeTab={activeTab}>
                                            <TabPane tabId={8} className="p-2 mt-2">
                                                <ContactsAccounts2
                                                    {...{
                                                        ...{
                                                            clientToken,
                                                            idToken,
                                                            companySettings,
                                                            user,
                                                            company: companyCtx.company,
                                                            refreshUser: () => { },
                                                            contactGUID: editID,
                                                            isActive: activeTab === 8,
                                                            selectedContactHouseholds,
                                                            contactAccountsLoading,
                                                            householdRows,
                                                            householdsLoading,
                                                            onRefreshContactAccounts: () => getContactAccounts(editID),
                                                            onRefreshHouseholds: getHouseholdInfo,
                                                            onAccountsChanged: onContactAccountsChanged
                                                        }
                                                    }} />
                                            </TabPane>
                                        </TabContent>
                                    </PermissionWrapper>
                                </PermissionWrapper>
                            )}

                            <PermissionWrapper userPermissions={userPermissions} allowedPermission="PDC Flow" hidden={true}>
                                <TabContent activeTab={activeTab}>
                                    <TabPane tabId={9} className="p-2 mt-2">
                                        <ContactsNDA2
                                            clientToken={clientToken}
                                            contactGUID={editID}
                                            user={user}
                                            contactSnapshot={ndaContactSnapshot}
                                            isActive={activeTab === 9}
                                        />
                                    </TabPane>
                                </TabContent>
                            </PermissionWrapper>
                        </Col>
                    </Row>
                </>
            ) : (

                <AddContact2
                    companies={companies}
                    addCategoryCommentsArray={addCategoryCommentsArray}
                    notesCategories={notesCategories}
                    handleAddSubmit={handleAddSubmit}
                    handleCancel={handleCancel}
                    setComment={setComment}
                    isFormBlank={isFormBlank}
                    unsavedChanges={unsavedChanges}
                    loading={loading}
                    closeModal={closeModal}
                    clientToken={clientToken}
                    userPermissions={user.permissions}
                    onSelectAutocomplete={onSelectAutocomplete}
                    removeCompany={removeCompany}
                />
            )
            }

        </Container >
    );
};

export default ContactsDetail2;
